import {engineContext} from './context.js';

(function(global){
'use strict';

const VERSION='0.1.0-alpha';

function freezeList(x){ return Object.freeze(Array.isArray(x)?x.slice():[]); }

function normalizeImpact(x){
  x=x||{};
  return Object.freeze({
    symbol:x.symbol||null,
    scope:['OWNED','WATCHLIST','RESEARCH'].includes(x.scope)?x.scope:'RESEARCH',
    level:['HIGH','MODERATE','LOW','UNKNOWN'].includes(x.level)?x.level:'UNKNOWN',
    topExposure:x.topExposure||null,
    impacts:freezeList(x.impacts),
    tradeSignal:false
  });
}

function alertPriority(impact){
  const i=normalizeImpact(impact);
  if(i.scope!=='OWNED') return 'NONE';
  if(i.level==='HIGH') return 'HIGH';
  if(i.level==='MODERATE') return 'MEDIUM';
  if(i.level==='UNKNOWN') return 'REVIEW';
  return 'NONE';
}

function alertForImpact(impact){
  const i=normalizeImpact(impact);
  const priority=alertPriority(i);
  if(priority==='NONE') return Object.freeze({
    emit:false,
    symbol:i.symbol,
    priority:'NONE',
    type:'NO_ALERT',
    message:null,
    tradeSignal:false
  });

  const region=i.topExposure&&i.topExposure.region?i.topExposure.region:null;
  const strength=i.topExposure&&typeof i.topExposure.exposureStrength==='number'
    ?i.topExposure.exposureStrength:null;

  let type='PERSONAL_EXPOSURE_REVIEW';
  let message='Owned position has market exposure that deserves review.';
  if(i.level==='HIGH'){
    message=region
      ?`Owned position has material exposure to stressed region: ${region}.`
      :'Owned position has material exposure to stressed market conditions.';
  }else if(i.level==='MODERATE'){
    message=region
      ?`Owned position has notable exposure to rotating/mixed region: ${region}.`
      :'Owned position has notable exposure to rotating/mixed market conditions.';
  }else if(i.level==='UNKNOWN'){
    type='PERSONAL_EXPOSURE_DATA_REVIEW';
    message='Owned position has insufficient verified regional exposure context.';
  }

  return Object.freeze({
    emit:true,
    symbol:i.symbol,
    priority,
    type,
    region,
    exposureStrength:strength,
    message,
    scope:i.scope,
    tradeSignal:false
  });
}

function buildAlerts(portfolioExposureResult){
  const p=portfolioExposureResult||{};
  const owned=Array.isArray(p.owned)?p.owned:[];
  return Object.freeze(
    owned.map(alertForImpact)
      .filter(a=>a.emit)
      .sort((a,b)=>{
        const rank={HIGH:3,MEDIUM:2,REVIEW:1,NONE:0};
        return rank[b.priority]-rank[a.priority];
      })
  );
}

function compress(alerts){
  const list=Array.isArray(alerts)?alerts:[];
  const groups=new Map();
  for(const a of list){
    const key=`${a.priority}|${a.region||'unknown'}`;
    if(!groups.has(key)) groups.set(key,[]);
    groups.get(key).push(a);
  }

  return Object.freeze([...groups.entries()].map(([key,items])=>{
    const [priority,region]=key.split('|');
    return Object.freeze({
      priority,
      region:region==='unknown'?null:region,
      count:items.length,
      symbols:Object.freeze(items.map(x=>x.symbol).filter(Boolean)),
      summary: region==='unknown'
        ?`${items.length} owned position${items.length===1?'':'s'} need exposure review.`
        :`${items.length} owned position${items.length===1?'':'s'} have notable exposure to ${region}.`,
      tradeSignal:false
    });
  }));
}

function uiSummary(portfolioExposureResult){
  const alerts=buildAlerts(portfolioExposureResult);
  const compressed=compress(alerts);
  return Object.freeze({
    alertCount:alerts.length,
    high:alerts.filter(a=>a.priority==='HIGH').length,
    medium:alerts.filter(a=>a.priority==='MEDIUM').length,
    review:alerts.filter(a=>a.priority==='REVIEW').length,
    alerts,
    compressed,
    personalOnly:true,
    tradeSignal:false
  });
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const ownedHigh={
    symbol:'AAA',scope:'OWNED',level:'HIGH',
    topExposure:{region:'china',exposureStrength:.52}
  };
  const ownedModerate={
    symbol:'BBB',scope:'OWNED',level:'MODERATE',
    topExposure:{region:'europe',exposureStrength:.35}
  };
  const watchHigh={
    symbol:'CCC',scope:'WATCHLIST',level:'HIGH',
    topExposure:{region:'china',exposureStrength:.60}
  };
  const ownedUnknown={
    symbol:'DDD',scope:'OWNED',level:'UNKNOWN'
  };

  ok('owned high becomes high alert',alertPriority(ownedHigh)==='HIGH');
  ok('owned moderate becomes medium alert',alertPriority(ownedModerate)==='MEDIUM');
  ok('watchlist never gets personal alert',alertPriority(watchHigh)==='NONE');
  ok('unknown owned exposure becomes review',alertPriority(ownedUnknown)==='REVIEW');
  let a=alertForImpact(ownedHigh);
  ok('high owned alert emitted',a.emit===true);
  ok('alert keeps region',a.region==='china');
  ok('alert never emits trade signal',a.tradeSignal===false);

  const summary=uiSummary({
    owned:[ownedHigh,ownedModerate,ownedUnknown],
    watchlist:[watchHigh]
  });
  ok('only owned alerts counted',summary.alertCount===3);
  ok('high count correct',summary.high===1);
  ok('medium count correct',summary.medium===1);
  ok('review count correct',summary.review===1);
  ok('personal-only flag explicit',summary.personalOnly===true);

  const compressed=compress([
    alertForImpact(ownedHigh),
    alertForImpact({symbol:'EEE',scope:'OWNED',level:'HIGH',topExposure:{region:'china',exposureStrength:.4}})
  ]);
  ok('same-region alerts compress',compressed.length===1&&compressed[0].count===2);
  ok('compressed output remains non-trading',compressed[0].tradeSignal===false);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={VERSION,normalizeImpact,alertPriority,alertForImpact,buildAlerts,compress,uiSummary,selfTest};
try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestPortfolioExposureAlertEngine=Object.freeze(API);

})(engineContext);
